import { Button, Card, Dialog, InfoTip, RotateCcw, Trash2 } from "@app/ui";
import { useEffect, useState } from "react";
import { useTrashStore } from "../../stores/index.js";
import { ShowMoreFooter, usePagedRows } from "../../lib/paged-list.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useI18n } from "../../lib/useI18n.js";
import { SettingsAnchor } from "./SettingsCard.js";

/** Settings → Trash: list soft-deleted tasks/events and restore them, delete
 *  them permanently, or empty the whole trash. Its own top-level category. */
export function TrashSettings() {
  const { t } = useI18n();
  const entries = useTrashStore((s) => s.entries);
  const busy = useTrashStore((s) => s.busy);
  const hydrate = useTrashStore((s) => s.hydrate);
  const restore = useTrashStore((s) => s.restore);
  const purge = useTrashStore((s) => s.purge);
  const emptyAll = useTrashStore((s) => s.emptyAll);
  const tf = useTimeFormat();

  // Pending confirm: a single entry id to purge, or "all" to empty the trash.
  const [confirm, setConfirm] = useState<string | "all" | null>(null);

  useEffect(() => { void hydrate(); }, [hydrate]);

  // Trash is unbounded by design — it holds everything deleted since the last
  // purge — so the list has to be paged like the other big ones.
  const { shown, total, hasMore, showMore } = usePagedRows(entries, null);

  const pendingEntry = entries.find((e) => e.id === confirm);

  return (
    <SettingsAnchor id="trash">
    <div className="flex flex-col gap-6">
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-1.5 font-medium">
            {t("common.trash")}
            <InfoTip label={t("common.moreInfo")}>{t("trash.description")}</InfoTip>
          </h2>
          {entries.length > 0 && (
            <Button variant="destructive" disabled={busy} onClick={() => setConfirm("all")}>
              <Trash2 className="mr-2 h-4 w-4" /> {t("trash.emptyTrash")}
            </Button>
          )}
        </div>

        {entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("trash.empty")}
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {shown.map((e) => (
              <li key={e.id} className="flex items-center gap-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm">{e.title || t("agenda.untitled")}</span>
                    <span className="shrink-0 rounded bg-accent px-1.5 py-0.5 text-xs text-muted-foreground">
                      {e.is_event ? t("common.event") : t("common.task")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("trash.deletedAt", { time: tf.stamp(e.deleted_at) })}</p>
                </div>
                <Button variant="outline" disabled={busy} onClick={() => void restore(e.id)}>
                  <RotateCcw className="mr-2 h-4 w-4" /> {t("common.restore")}
                </Button>
                <Button
                  variant="ghost"
                  disabled={busy}
                  aria-label={t("trash.deletePermanently")}
                  onClick={() => setConfirm(e.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
            {hasMore && <li><ShowMoreFooter shown={shown.length} total={total} onShowMore={showMore} className="border-t-0 px-0" /></li>}
          </ul>
        )}
      </Card>

      <Dialog
        open={confirm != null}
        onClose={() => setConfirm(null)}
        title={confirm === "all" ? t("trash.emptyTrash") : t("trash.deletePermanently")}
        className="max-w-md"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground">
            {confirm === "all"
              ? t("trash.emptyConfirm", { count: entries.length })
              : t("trash.deleteConfirm", { title: pendingEntry?.title || t("agenda.untitled") })}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                const target = confirm;
                setConfirm(null);
                if (target === "all") void emptyAll();
                else if (target) void purge(target);
              }}
            >
              {confirm === "all" ? t("trash.emptyTrash") : t("trash.deletePermanently")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirm(null)}>{t("common.cancel")}</Button>
          </div>
        </div>
      </Dialog>
    </div>
    </SettingsAnchor>
  );
}
